// Shared file picker for bug reports and support tickets.
//
// Single implementation for every "attach files to a form" surface: image
// thumbnails with a lightbox, non-image files as compact chips, clipboard
// paste, and an optional auto-captured screenshot slot (bug reporter only).

import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, Upload, Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";

/** Non-image types we accept alongside images. */
export const DOCUMENT_ACCEPT =
  ".pdf,.txt,.csv,.log,.json,.doc,.docx,.zip";

export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024; // 15MB per file

interface AttachmentUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** Auto-captured screenshot rendered as a pinned first tile. */
  autoCapture?: Blob | null;
  onRemoveAutoCapture?: () => void;
  maxFiles?: number;
  label?: string;
  buttonLabel?: string;
  /** When false, documents are accepted too. Defaults to images-only. */
  imagesOnly?: boolean;
}

export function AttachmentUpload({
  files,
  onFilesChange,
  autoCapture = null,
  onRemoveAutoCapture,
  maxFiles = 3,
  label = "Screenshots",
  buttonLabel = "Add Screenshot",
  imagesOnly = true,
}: AttachmentUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const autoCaptureUrl = autoCapture ? URL.createObjectURL(autoCapture) : null;

  const totalCount = files.length + (autoCapture ? 1 : 0);
  const canAddMore = totalCount < maxFiles;
  const accept = imagesOnly ? "image/*" : `image/*,${DOCUMENT_ACCEPT}`;

  const handleFiles = useCallback(
    (incoming: FileList | File[] | null) => {
      if (!incoming) return;
      const allowed = maxFiles - totalCount;
      if (allowed <= 0) {
        toast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }
      const list = Array.from(incoming as ArrayLike<File>);
      const accepted = list
        .filter((f) => (imagesOnly ? f.type.startsWith("image/") : true))
        .filter((f) => {
          if (f.size > MAX_ATTACHMENT_BYTES) {
            toast.error(`${f.name} is larger than 15MB`);
            return false;
          }
          return true;
        })
        .slice(0, allowed);
      if (accepted.length === 0) {
        if (imagesOnly) toast.error("Please select image files only");
        return;
      }
      onFilesChange([...files, ...accepted]);
    },
    [files, totalCount, maxFiles, imagesOnly, onFilesChange],
  );

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const pasted: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) pasted.push(file);
        }
      }
      if (pasted.length > 0) {
        e.preventDefault();
        handleFiles(pasted);
      }
    },
    [handleFiles],
  );

  const images = files.filter((f) => f.type.startsWith("image/"));
  const documents = files.filter((f) => !f.type.startsWith("image/"));

  return (
    <div className="space-y-2" onPaste={handlePaste}>
      <label className="text-sm font-medium">
        {label} ({totalCount}/{maxFiles})
      </label>

      {(autoCapture || images.length > 0) && (
        <div className="grid grid-cols-3 gap-2">
          {autoCaptureUrl && (
            <div className="relative rounded-md border overflow-hidden aspect-video bg-muted">
              <button
                type="button"
                onClick={() => setPreviewSrc(autoCaptureUrl)}
                className="w-full h-full cursor-pointer"
              >
                <img
                  src={autoCaptureUrl}
                  alt="Auto-captured screenshot"
                  className="w-full h-full object-cover"
                />
              </button>
              <div className="absolute top-1 left-1">
                <span className="text-[10px] bg-accent text-accent-foreground px-1.5 py-0.5 rounded">
                  Auto
                </span>
              </div>
              {onRemoveAutoCapture && (
                <button
                  type="button"
                  aria-label="Remove auto-captured screenshot"
                  onClick={onRemoveAutoCapture}
                  className="absolute top-1 right-1 p-0.5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          )}
          {images.map((file) => {
            const objUrl = URL.createObjectURL(file);
            return (
              <div
                key={`${file.name}-${file.lastModified}`}
                className="relative rounded-md border overflow-hidden aspect-video bg-muted"
              >
                <button
                  type="button"
                  onClick={() => setPreviewSrc(objUrl)}
                  className="w-full h-full cursor-pointer"
                >
                  <img
                    src={objUrl}
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => removeFile(files.indexOf(file))}
                  className="absolute top-1 right-1 p-0.5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {documents.length > 0 && (
        <ul className="space-y-1">
          {documents.map((file) => (
            <li
              key={`${file.name}-${file.lastModified}`}
              className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm"
            >
              <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="truncate flex-1">{file.name}</span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() => removeFile(files.indexOf(file))}
                className="p-1 rounded-full text-muted-foreground hover:text-destructive min-h-[44px] min-w-[44px] flex items-center justify-center tablet:min-h-0 tablet:min-w-0"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {canAddMore && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            {imagesOnly ? (
              <Upload className="mr-2 h-3.5 w-3.5" />
            ) : (
              <Paperclip className="mr-2 h-3.5 w-3.5" />
            )}
            {buttonLabel}
          </Button>
          <p className="text-xs text-muted-foreground mt-1">
            Or paste an image from your clipboard (Ctrl+V)
          </p>
        </div>
      )}

      <Dialog open={!!previewSrc} onOpenChange={() => setPreviewSrc(null)}>
        <DialogContent className="max-w-4xl p-2">
          {previewSrc && (
            <img
              src={previewSrc}
              alt="Attachment preview"
              className="w-full h-auto rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
